import { useEffect, useRef } from "react";

import { getCookie } from "cookies-next";

import analyticsIntegrations from "~/app/analytics/analytics-client";
import { useProfileSSOid } from "~/queries/profile/useProfile";
import { SSO_ID_COOKIE } from "~/utils/constants";

/**
 * Attaches a Segment click-tracking listener to a "create space" anchor element.
 *
 * We need it to make sure tracking request finsihes before navigation is done.
 * Delay is being done automatically by Segment's `trackLink()` method,
 * no onClick handlers needed.
 *
 * Returns a ref to attach to the `<a>` element. The listener is registered once
 * on mount;
 *
 * @param source - The page section that owns the link, used as the event source.
 * @returns A ref to pass to the `<a>` element that should be tracked.
 */
const useCreateSpaceLinkTracking = (
  source: "hero_section" | "cta_banner_section",
) => {
  const ssoId = useProfileSSOid();
  const linkRef = useRef<HTMLAnchorElement>(null);

  // Ref instead of effect dependency — re-registering trackLink on ssoId
  // changes would stack duplicate Segment listeners on the same element.
  const ssoIdRef = useRef(ssoId);

  // This flag prevents double-invoke of register the listeners.
  const trackingRegistered = useRef(false);
  ssoIdRef.current = ssoId;

  useEffect(() => {
    if (!linkRef.current || trackingRegistered.current) return;
    trackingRegistered.current = true;

    // Pass ssoId as a callback so it is read at click time, not mount time.
    analyticsIntegrations?.trackCreateSpaceLinkClickEvent(
      source,
      linkRef.current,
      ssoIdRef.current ?? (getCookie(SSO_ID_COOKIE) as string | undefined),
    );
  }, [source]);

  return linkRef;
};

export { useCreateSpaceLinkTracking };
